import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { TUTORIAL_PREAMBLE, TUTORIAL_SECTIONS } from '../data/tutorial';
import './TutorialPage.css';

/**
 * TutorialPage — a página de **Ajuda** in-app (rota /ajuda, item no menu
 * lateral). É o manual de referência do usuário, recortado em seções de
 * acordeão (uma por seção "##" do tutorial canônico). O usuário expande só o
 * que precisa.
 *
 * NÃO confundir com o micro-tour de boas-vindas (#239, WelcomeTour) — aquele é
 * o onboarding de primeiro acesso e continua existindo; esta página é o
 * manual completo, consultável a qualquer momento.
 *
 * Conteúdo = dado estruturado curado em src/data/tutorial.js (cópia do
 * docs/TUTORIAL_USO.md do PR #232). O body de cada seção é markdown,
 * renderizado por <ReactMarkdown> (já é dependência do projeto — sem lib nova).
 *
 * Atenção (react-markdown v10): o componente não aceita mais prop `className`.
 * Estilizamos via wrapper <div className="tutorial-md">.
 */

export default function TutorialPage() {
  // Primeira seção aberta por padrão; as demais colapsadas. O usuário expande
  // o que quiser (acordeão multi-open — não fechamos as outras ao abrir uma).
  const [openIds, setOpenIds] = useState(() => {
    const first = TUTORIAL_SECTIONS[0];
    return first ? { [first.id]: true } : {};
  });

  const toggle = (id) => {
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const allOpen = TUTORIAL_SECTIONS.every((s) => openIds[s.id]);

  const setAll = (open) => {
    const next = {};
    if (open) {
      TUTORIAL_SECTIONS.forEach((s) => {
        next[s.id] = true;
      });
    }
    setOpenIds(next);
  };

  return (
    <div className="tutorial-page">
      <header className="tutorial-header">
        <h1 className="tutorial-title">Ajuda</h1>
        <p className="tutorial-subtitle">
          Guia de uso da Qaa.sa — do primeiro acesso ao fechamento da NC. Abra a
          seção que você precisa.
        </p>
        <div className="tutorial-intro tutorial-md">
          <ReactMarkdown>{TUTORIAL_PREAMBLE}</ReactMarkdown>
        </div>
        <div className="tutorial-controls">
          <button
            type="button"
            className="tutorial-control-btn"
            onClick={() => setAll(!allOpen)}
          >
            {allOpen ? 'Recolher tudo' : 'Expandir tudo'}
          </button>
        </div>
      </header>

      <div className="tutorial-accordion">
        {TUTORIAL_SECTIONS.map((section, idx) => {
          const isOpen = !!openIds[section.id];
          const panelId = `tutorial-panel-${section.id}`;
          const btnId = `tutorial-btn-${section.id}`;
          return (
            <section
              key={section.id}
              id={section.id}
              className={`tutorial-section ${isOpen ? 'is-open' : ''}`}
            >
              <h2 className="tutorial-section-heading">
                <button
                  id={btnId}
                  type="button"
                  className="tutorial-section-toggle"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggle(section.id)}
                >
                  <span className="tutorial-section-num">{idx + 1}</span>
                  <span className="tutorial-section-name">{section.title}</span>
                  <span
                    className="material-symbols-outlined tutorial-section-chevron"
                    aria-hidden="true"
                  >
                    expand_more
                  </span>
                </button>
              </h2>
              {isOpen && (
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={btnId}
                  className="tutorial-section-body tutorial-md"
                >
                  <ReactMarkdown>{section.body}</ReactMarkdown>
                </div>
              )}
            </section>
          );
        })}
      </div>

      <footer className="tutorial-footer">
        As telas e os rótulos podem evoluir entre versões; em caso de
        divergência, a interface vigente prevalece. Precisa de ajuda que não
        está aqui? Use <strong>“Falar com o suporte”</strong> no topo da tela.
      </footer>
    </div>
  );
}
